import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { PARTICIPANTS, ALL_DEALS } from './communicationsData';

export default function NewMessageModal({ onClose, onSend }) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [subject, setSubject] = useState('');
  const [linkedDeal, setLinkedDeal] = useState('');
  const [body, setBody] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const filteredUsers = PARTICIPANTS?.filter(p =>
    p?.name?.toLowerCase()?.includes(userSearch?.toLowerCase()) &&
    !selectedUsers?.find(u => u?.id === p?.id)
  );

  const addUser = (user) => {
    setSelectedUsers(prev => [...prev, user]);
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const removeUser = (id) => setSelectedUsers(prev => prev?.filter(u => u?.id !== id));

  const ROLE_BADGE = {
    investor: 'bg-[rgba(59,76,76,0.07)] text-blue-700',
    sponsor: 'bg-emerald-50 text-emerald-700',
    admin: 'bg-purple-50 text-purple-700',
  };

  const handleSend = () => {
    if (!subject?.trim() || !body?.trim() || selectedUsers?.length === 0) return;
    onSend({ to: selectedUsers, subject, linkedDeal, body });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">New Message</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* To field */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">To</label>
            <div className="flex flex-wrap gap-1.5 p-2 border border-slate-200 rounded-xl min-h-10 focus-within:ring-2 focus-within:ring-[#3b4c4c]/20 focus-within:border-[#3b4c4c]/40 bg-slate-50">
              {selectedUsers?.map(u => (
                <span key={u?.id} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#3b4c4c]/8 text-[#3b4c4c] text-xs font-medium">
                  {u?.name}
                  <span className={`ml-1 px-1 py-0.5 rounded text-xs ${ROLE_BADGE?.[u?.role]}`}>{u?.role}</span>
                  <button onClick={() => removeUser(u?.id)} className="ml-0.5 hover:text-red-500 transition-colors">
                    <Icon name="X" size={10} />
                  </button>
                </span>
              ))}
              <div className="relative flex-1 min-w-24">
                <input
                  type="text"
                  placeholder={selectedUsers?.length === 0 ? 'Search participants…' : ''}
                  value={userSearch}
                  onChange={e => { setUserSearch(e?.target?.value); setShowUserDropdown(true); }}
                  onFocus={() => setShowUserDropdown(true)}
                  className="w-full text-sm bg-transparent outline-none placeholder-slate-400 py-0.5"
                />
                {showUserDropdown && userSearch && filteredUsers?.length > 0 && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-lg z-10 overflow-hidden">
                    {filteredUsers?.map(u => (
                      <button
                        key={u?.id}
                        onClick={() => addUser(u)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 transition-colors text-left"
                      >
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                          style={{ background: u?.color }}
                        >
                          {u?.initials}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{u?.name}</p>
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${ROLE_BADGE?.[u?.role]}`}>{u?.role}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Thread Name / Subject</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e?.target?.value)}
              placeholder="Enter thread name…"
              className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/20 focus:border-[#3b4c4c]/40 placeholder-slate-400"
            />
          </div>

          {/* Link to deal */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Link to Deal / SPV <span className="font-normal text-slate-400">(optional)</span></label>
            <select
              value={linkedDeal}
              onChange={e => setLinkedDeal(e?.target?.value)}
              className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/20 appearance-none"
            >
              <option value="">No deal linked</option>
              {ALL_DEALS?.map(d => (
                <option key={d?.id} value={d?.id}>{d?.name} — {d?.stage}</option>
              ))}
            </select>
          </div>

          {/* Message body */}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Message</label>
            <textarea
              value={body}
              onChange={e => setBody(e?.target?.value)}
              placeholder="Write your message…"
              rows={4}
              className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/20 focus:border-[#3b4c4c]/40 placeholder-slate-400"
            />
          </div>

          {/* Attach */}
          <button className="flex items-center gap-2 text-xs text-slate-500 hover:text-[#3b4c4c] transition-colors">
            <Icon name="Paperclip" size={13} />
            Attach document
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!subject?.trim() || !body?.trim() || selectedUsers?.length === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              subject?.trim() && body?.trim() && selectedUsers?.length > 0
                ? 'bg-[#3b4c4c] text-white hover:bg-[#162d4a]'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            <Icon name="Send" size={14} />
            Send Message
          </button>
        </div>
      </div>
    </div>
  );
}
